import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material";

import CurrencyCell from "../../component/CurrencyCell";
import { monthNames } from "../../../../component/constent";
import React from "react";

const parseAmount = (value) => {
  if (value == null) return 0;

  let str = String(value).replace(/,/g, "").trim();

  // Handle trailing minus
  if (str.endsWith("-")) {
    str = "-" + str.slice(0, -1);
  }

  return Number(str) || 0;
};

export default function WGRTable({
  rows = [],
  shop,
  totals,
  showTotal = true,
  month,
  year,
}) {
  const getMonthName = (month) => {
    const monthNumber = Number(month);

    return (
      monthNames?.[monthNumber] ||
      monthNames?.[monthNumber - 1] ||
      ""
    );
  };

  /*
   * Example:
   * month = 3
   * year = 2026
   *
   * This produces:
   * JAN-2026 to MAR-2026
   */
  const startMonth =
    month >= 3 ? Number(month) - 2 : Number(month);

  const period =
    month && year
      ? `${getMonthName(startMonth).substring(0, 3).toUpperCase()}-${year}
         to
         ${getMonthName(month).substring(0, 3).toUpperCase()}-${year}`
      : "";

  /*
   * PDF has three groups:
   *
   * PCV
   * OCV
   * LHB
   *
   * We use ActivityType as the group field.
   */
  const groupedRows = rows.reduce((groups, row) => {
    const group = row.ActivityType || "PCV";

    if (!groups[group]) {
      groups[group] = [];
    }

    groups[group].push(row);

    return groups;
  }, {});

  const sectionOrder = ["PCV", "OCV", "LHB"];

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        px: 2,
        py: 2,
      }}
    >
      {/* ================= TITLE ================= */}
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            lineHeight: 1.3,
          }}
        >
          Quarterly Statement of POH Expenditure of Mechanical Workshop Gorakhpur
          <br />
          {period}
        </Typography>
      </Box>

      {/* ================= TABLE ================= */}
      <TableContainer>
        <Table
          size="small"
          sx={{
            width: "95%",
            borderCollapse: "collapse",

            "& th, & td": {
              border: "1px solid #222",
              fontSize: "13px",
              padding: "7px 8px",
            },

            "& th": {
              fontWeight: 700,
              textAlign: "center",
              verticalAlign: "middle",
            },
          }}
        >
          {/* ================= HEADER ================= */}
          <TableHead>
            {/* First header row */}
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  width: "27%",
                }}
              >
                Activity
              </TableCell>

              <TableCell
                rowSpan={2}
                sx={{
                  width: "12%",
                }}
              >
                Labour Per
                <br />
                Vechicle
              </TableCell>

              <TableCell
                rowSpan={2}
                sx={{
                  width: "13%",
                }}
              >
                Material Per
                <br />
                Vechicle
              </TableCell>

              {/* IMPORTANT:
                  Merged On Cost header
              */}
              <TableCell
                colSpan={2}
                sx={{
                  width: "23%",
                }}
              >
                On Cost
              </TableCell>

              <TableCell
                rowSpan={2}
                sx={{
                  width: "13%",
                }}
              >
                Unit Cost
                <br />
                per
                <br />
                Vechicle
                <br />
                {"{ 000 }"}
              </TableCell>

              <TableCell
                rowSpan={2}
                sx={{
                  width: "12%",
                }}
              >
                Total Out Tern
              </TableCell>
            </TableRow>

            {/* Second header row */}
            <TableRow>
              <TableCell>
                Labour Per
                <br />
                Vechicle
              </TableCell>

              <TableCell>
                Material Per
                <br />
                Vechicle
              </TableCell>
            </TableRow>
          </TableHead>

          {/* ================= BODY ================= */}
          <TableBody>
            {sectionOrder.map((section) => {
              const sectionRows = groupedRows[section];

              if (!sectionRows?.length) {
                return null;
              }

              return (
                <React.Fragment key={section}>
                  {/* SECTION HEADER */}
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        fontWeight: 700,
                        fontSize: "14px",
                        textAlign: "left",
                        backgroundColor: "#fff",
                        py: 0.8,
                      }}
                    >
                      {section}
                    </TableCell>
                  </TableRow>

                  {/* SECTION DATA */}
                  {sectionRows.map((row, index) => (
                    <TableRow key={`${section}-${index}`}>
                      {/* Activity */}
                      <TableCell
                        sx={{
                          textAlign: "left",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.POHFullDescription ||
                          row.Activity ||
                          row.ActivityType}
                      </TableCell>

                      {/* Labour */}
                      <TableCell align="right">
                        <CurrencyCell value={Number(row.Expr1) * Number(row.OutTern)} />
                      </TableCell>

                      {/* Material */}
                      <TableCell align="right">
                        <CurrencyCell value={Number(row.Expr2) * Number(row.OutTern)} />
                      </TableCell>

                      {/* On Cost Labour */}
                      <TableCell align="right">
                        <CurrencyCell value={Number(row.Expr3) * Number(row.OutTern)} />
                      </TableCell>

                      {/* On Cost Material */}
                      <TableCell align="right">
                        <CurrencyCell value={Number(row.Expr4) * Number(row.OutTern)} />
                      </TableCell>

                      {/* Unit Cost */}
                      <TableCell align="right">
                        <CurrencyCell value={Number(row.Total) * Number(row.OutTern)} />
                      </TableCell>

                      {/* Total Out Turn */}
                      <TableCell align="right">
                        <CurrencyCell value={row.OutTern} />
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ================= FOOTER ================= */}
      <Box
        sx={{
          mt: 3,
          fontWeight: 700,
          fontSize: "13px",
        }}
      >
        Forwarded to Dy FA& CAO/S&W for information.
      </Box>

      {/* ================= SIGNATURES ================= */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 7,
          px: 2,
          fontWeight: 700,
          fontSize: "13px",
        }}
      >
        <Box>CWM</Box>

        <Box sx={{ marginRight: 10 }}>Sr AFA /W</Box>
      </Box>
    </Box>
  );
}